/**
 * タイムラインのスタンプボタン
 *
 * ラリーに属さない場所にはスタンプの概念がないので何も出さない。
 * 同じ場所が複数のラリーに入っているときは、道の駅のスタンプは押せたがピンバッジは
 * 品切れ、のようにラリーごとに結果が変わるので、ボタンの隣に選択を開く。
 */

import type { PlanItemRallyLink, RoutePlanItem } from '@/api/types/route-plan'
import { RALLY_FALLBACK_COLOR } from '@/components/shared/map/use-rally-places'
import { StampFace } from '@/components/shared/stamp-face'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { parseUtcDate } from '@/lib/date-utils'

import { hasRally, isFullyStamped, stampedCount } from '../../lib'

type Props = {
  item: RoutePlanItem
  onToggle: (item: RoutePlanItem, links: PlanItemRallyLink[], stamped: boolean) => void
}

const stampedLabel = (value: string): string =>
  parseUtcDate(value).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })

export const StampButton = ({ item, onToggle }: Props) => {
  if (!hasRally(item)) return null

  const links = item.rally_items
  const stamped = isFullyStamped(item)

  if (links.length === 1) {
    return (
      <button
        onClick={() => onToggle(item, links, !stamped)}
        aria-label={stamped ? 'スタンプを解除' : 'スタンプを押す'}
      >
        <StampFace stamped={stamped} />
      </button>
    )
  }

  return (
    <Popover>
      <PopoverTrigger aria-label="ラリーごとのスタンプを選ぶ">
        <StampFace stamped={stamped} badge={`${stampedCount(item)}/${links.length}`} />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-1 p-2">
        <p className="px-1 pb-1 text-xs text-muted-foreground">{links.length} つのラリーに登録されています</p>
        {links.map((link) => (
          <button
            key={link.item_id}
            onClick={() => onToggle(item, [link], link.stamped_at === null)}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left active:bg-accent"
          >
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: link.rally_color ?? RALLY_FALLBACK_COLOR }}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium">{link.rally_title}</span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {link.stamped_at ? `${stampedLabel(link.stamped_at)} に獲得` : '未獲得'}
              </span>
            </span>
            <StampFace stamped={link.stamped_at !== null} />
          </button>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() =>
            onToggle(
              item,
              links.filter((link) => (link.stamped_at !== null) === stamped),
              !stamped,
            )
          }
        >
          {stamped ? 'すべて外す' : 'すべて押す'}
        </Button>
      </PopoverContent>
    </Popover>
  )
}
